/** React-window list ref type (has scrollTo and internal refs) */
interface ListLike {
  scrollTo?: (offset: number) => void;
  state?: { scrollOffset: number };
  props?: { itemCount?: number; height?: number };
  _outerRef?: HTMLElement | null;
  _listRef?: HTMLElement | null;
  outerRef?: HTMLElement | null;
}

/**
 * Smoothly animate scroll position of a react-window list.
 */
export function smoothScroll(
  list: ListLike | null,
  targetOffset: number,
  duration = 300,
  startOffset?: number
): void {
  if (!list || typeof list.scrollTo !== "function") return;
  const start = startOffset ?? list.state?.scrollOffset ?? 0;
  const change = targetOffset - start;
  const startTime = performance.now();

  function animate(now: number): void {
    const elapsed = now - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const ease =
      progress < 0.5
        ? 2 * progress * progress
        : -1 + (4 - 2 * progress) * progress;

    if (list?.scrollTo) {
      list.scrollTo(start + change * ease);
    }

    if (elapsed < duration) {
      requestAnimationFrame(animate);
    }
  }
  requestAnimationFrame(animate);
}

/**
 * Scroll react-window list so the item at index is centered (or near center).
 */
export function safeScrollToIndex(
  listRef: { current: ListLike | null } | null,
  index: number,
  rowHeight: number
): void {
  if (!listRef?.current || typeof index !== "number" || index < 0) return;
  const list = listRef.current;
  const outer =
    list._outerRef ?? list._listRef ?? (list as ListLike & { outerRef?: HTMLElement }).outerRef;
  const itemCount = list.props?.itemCount ?? 0;
  const containerHeight =
    (outer as HTMLElement | undefined)?.clientHeight ?? list.props?.height ?? 0;

  const totalHeight = itemCount * rowHeight;
  const desiredOffset =
    index * rowHeight - Math.floor((containerHeight - rowHeight) / 2);
  const maxOffset = Math.max(0, totalHeight - containerHeight);
  const scrollOffset = Math.max(0, Math.min(desiredOffset, maxOffset));

  if (list && typeof list.scrollTo === "function") {
    smoothScroll(list, scrollOffset);
  } else if (outer && typeof (outer as HTMLElement).scrollTo === "function") {
    (outer as HTMLElement).scrollTo({ top: scrollOffset, behavior: "smooth" });
  }
}
